import React, { useEffect, useState } from 'react';
import { ayarAPI, ilceAPI, authAPI } from '../api';
import toast from 'react-hot-toast';
import { Save, Plus, Trash2, Eye, EyeOff, MapPin, Factory, Lock, Palette } from 'lucide-react';

const FONTS = ['Outfit', 'Inter', 'DM Sans', 'Space Grotesk', 'Poppins', 'Nunito'];

function Section({ title, icon: Icon, children }) {
  return (
    <div className="card p-6">
      <div className="flex items-center gap-2 mb-5 pb-4 border-b border-navy-100">
        <div className="w-8 h-8 rounded-lg bg-navy-100 flex items-center justify-center">
          <Icon size={15} className="text-navy-600" />
        </div>
        <h2 className="font-semibold text-navy-700">{title}</h2>
      </div>
      {children}
    </div>
  );
}

export default function AyarlarPage() {
  const [ayarlar, setAyarlar] = useState({});
  const [ilceler, setIlceler] = useState([]);
  const [ilceLoading, setIlceLoading] = useState(false);
  const [savingAyar, setSavingAyar] = useState(false);

  const [newIlce, setNewIlce] = useState('');
  const [newIlceTip, setNewIlceTip] = useState('ilce');

  const [pwForm, setPwForm] = useState({ current: '', next: '', confirm: '' });
  const [showPw, setShowPw] = useState(false);
  const [savingPw, setSavingPw] = useState(false);

  useEffect(() => {
    ayarAPI.list().then((r) => {
      const map = {};
      r.data.forEach((a) => { map[a.key] = a.value; });
      setAyarlar(map);
    });
    loadIlceler();
  }, []);

  const loadIlceler = () => {
    setIlceLoading(true);
    ilceAPI.list()
      .then((r) => setIlceler(r.data))
      .finally(() => setIlceLoading(false));
  };

  const handleSaveAyarlar = async () => {
    setSavingAyar(true);
    try {
      await ayarAPI.updateMultiple(ayarlar);
      toast.success('Ayarlar kaydedildi');
    } catch {
      toast.error('Kaydedilemedi');
    } finally {
      setSavingAyar(false);
    }
  };

  const handleAddIlce = async (e) => {
    e.preventDefault();
    if (!newIlce.trim()) return;
    try {
      await ilceAPI.create({ ad: newIlce.trim(), tip: newIlceTip });
      toast.success(`"${newIlce}" eklendi`);
      setNewIlce('');
      loadIlceler();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Eklenemedi');
    }
  };

  const handleDeleteIlce = async (id, ad) => {
    if (!window.confirm(`"${ad}" silinsin mi?`)) return;
    try {
      await ilceAPI.delete(id);
      toast.success('Silindi');
      loadIlceler();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Silinemedi');
    }
  };

  const handleChangePassword = async (e) => {
    e.preventDefault();
    if (pwForm.next.length < 6) { toast.error('Yeni şifre en az 6 karakter olmalı'); return; }
    if (pwForm.next !== pwForm.confirm) { toast.error('Şifreler eşleşmiyor'); return; }
    setSavingPw(true);
    try {
      await authAPI.changePassword(pwForm.current, pwForm.next);
      toast.success('Şifre değiştirildi');
      setPwForm({ current: '', next: '', confirm: '' });
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Şifre değiştirilemedi');
    } finally {
      setSavingPw(false);
    }
  };

  const ilceler_only = ilceler.filter((i) => i.tip === 'ilce');
  const osb_only = ilceler.filter((i) => i.tip === 'osb');

  return (
    <div className="max-w-3xl space-y-6 animate-fade-in">
      <div>
        <h1 className="text-2xl font-display text-navy-800">Ayarlar</h1>
        <p className="text-navy-400 text-sm mt-0.5">Panel yapılandırması ve veriler</p>
      </div>

      {/* Görünüm */}
      <Section title="Görünüm & Kişiselleştirme" icon={Palette}>
        <div className="space-y-4">
          <div>
            <label className="label">Panel Adı</label>
            <input
              className="input"
              value={ayarlar.panel_adi || ''}
              onChange={(e) => setAyarlar({ ...ayarlar, panel_adi: e.target.value })}
              placeholder="Firma Paneli"
            />
          </div>

          <div>
            <label className="label">Font</label>
            <div className="flex flex-wrap gap-2">
              {FONTS.map((f) => (
                <button
                  key={f}
                  type="button"
                  onClick={() => setAyarlar({ ...ayarlar, font: f })}
                  className={`px-4 py-2 rounded-xl border text-sm transition-all
                    ${ayarlar.font === f
                      ? 'border-navy-600 bg-navy-600 text-white'
                      : 'border-navy-200 text-navy-600 hover:border-navy-400'}`}
                  style={{ fontFamily: f }}
                >
                  {f}
                </button>
              ))}
            </div>
          </div>

          <div className="grid grid-cols-3 gap-4">
            {[
              { key: 'renk_birincil', label: 'Birincil Renk' },
              { key: 'renk_ikincil', label: 'İkincil Renk' },
              { key: 'renk_arkaplan', label: 'Arkaplan' },
            ].map(({ key, label }) => (
              <div key={key}>
                <label className="label">{label}</label>
                <div className="flex items-center gap-2">
                  <input
                    type="color"
                    value={ayarlar[key] || '#000000'}
                    onChange={(e) => setAyarlar({ ...ayarlar, [key]: e.target.value })}
                    className="w-10 h-10 rounded-lg cursor-pointer border border-navy-200 p-0.5"
                  />
                  <input
                    className="input font-mono text-xs flex-1"
                    value={ayarlar[key] || ''}
                    onChange={(e) => setAyarlar({ ...ayarlar, [key]: e.target.value })}
                    placeholder="#000000"
                  />
                </div>
              </div>
            ))}
          </div>

          <div className="flex justify-end pt-2">
            <button className="btn-primary" onClick={handleSaveAyarlar} disabled={savingAyar}>
              {savingAyar && <span className="w-3.5 h-3.5 border-2 border-white/40 border-t-white rounded-full animate-spin" />}
              <Save size={15} /> Kaydet
            </button>
          </div>
        </div>
      </Section>

      {/* İlçe/OSB yönetimi */}
      <Section title="İlçe / OSB Yönetimi" icon={MapPin}>
        {/* Ekleme formu */}
        <form onSubmit={handleAddIlce} className="flex gap-2 mb-5">
          <select
            className="select w-28"
            value={newIlceTip}
            onChange={(e) => setNewIlceTip(e.target.value)}
          >
            <option value="ilce">İlçe</option>
            <option value="osb">OSB</option>
          </select>
          <input
            className="input flex-1"
            placeholder="Ad girin…"
            value={newIlce}
            onChange={(e) => setNewIlce(e.target.value)}
          />
          <button type="submit" className="btn-primary">
            <Plus size={16} /> Ekle
          </button>
        </form>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
          {/* İlçeler */}
          <div>
            <div className="flex items-center gap-2 mb-2">
              <MapPin size={14} className="text-navy-500" />
              <p className="text-sm font-semibold text-navy-600">
                İlçeler ({ilceler_only.length})
              </p>
            </div>
            <div className="space-y-1 max-h-64 overflow-y-auto">
              {ilceLoading ? (
                <p className="text-xs text-navy-400 py-4 text-center">Yükleniyor…</p>
              ) : ilceler_only.length === 0 ? (
                <p className="text-xs text-navy-400 py-4 text-center">Henüz ilçe yok</p>
              ) : ilceler_only.map((i) => (
                <div key={i.id} className="flex items-center justify-between px-3 py-2 rounded-lg hover:bg-navy-50 group">
                  <span className="text-sm text-navy-700">{i.ad}</span>
                  <button
                    className="opacity-0 group-hover:opacity-100 btn-ghost p-1 text-rose-500 hover:text-rose-700"
                    onClick={() => handleDeleteIlce(i.id, i.ad)}
                  >
                    <Trash2 size={13} />
                  </button>
                </div>
              ))}
            </div>
          </div>

          {/* OSB'ler */}
          <div>
            <div className="flex items-center gap-2 mb-2">
              <Factory size={14} className="text-navy-500" />
              <p className="text-sm font-semibold text-navy-600">
                OSB'ler ({osb_only.length})
              </p>
            </div>
            <div className="space-y-1 max-h-64 overflow-y-auto">
              {ilceLoading ? (
                <p className="text-xs text-navy-400 py-4 text-center">Yükleniyor…</p>
              ) : osb_only.length === 0 ? (
                <p className="text-xs text-navy-400 py-4 text-center">Henüz OSB yok</p>
              ) : osb_only.map((i) => (
                <div key={i.id} className="flex items-center justify-between px-3 py-2 rounded-lg hover:bg-navy-50 group">
                  <div className="flex items-center gap-2">
                    <span className="badge bg-navy-100 text-navy-600 text-xs">OSB</span>
                    <span className="text-sm text-navy-700">{i.ad}</span>
                  </div>
                  <button
                    className="opacity-0 group-hover:opacity-100 btn-ghost p-1 text-rose-500 hover:text-rose-700"
                    onClick={() => handleDeleteIlce(i.id, i.ad)}
                  >
                    <Trash2 size={13} />
                  </button>
                </div>
              ))}
            </div>
          </div>
        </div>
      </Section>

      {/* Şifre değiştirme */}
      <Section title="Şifre Değiştir" icon={Lock}>
        <form onSubmit={handleChangePassword} className="space-y-4 max-w-sm">
          {[
            { key: 'current', label: 'Mevcut Şifre', placeholder: '••••••••' },
            { key: 'next', label: 'Yeni Şifre', placeholder: 'En az 6 karakter' },
            { key: 'confirm', label: 'Yeni Şifre (Tekrar)', placeholder: '••••••••' },
          ].map(({ key, label, placeholder }) => (
            <div key={key}>
              <label className="label">{label}</label>
              <div className="relative">
                <input
                  type={showPw ? 'text' : 'password'}
                  className="input pr-10"
                  placeholder={placeholder}
                  value={pwForm[key]}
                  onChange={(e) => setPwForm({ ...pwForm, [key]: e.target.value })}
                  required
                />
                {key === 'next' && (
                  <button
                    type="button"
                    onClick={() => setShowPw(!showPw)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-navy-400 hover:text-navy-600"
                  >
                    {showPw ? <EyeOff size={15} /> : <Eye size={15} />}
                  </button>
                )}
              </div>
            </div>
          ))}

          <button type="submit" className="btn-primary" disabled={savingPw}>
            {savingPw && <span className="w-3.5 h-3.5 border-2 border-white/40 border-t-white rounded-full animate-spin" />}
            <Lock size={15} /> Şifreyi Güncelle
          </button>
        </form>
      </Section>

      {/* API Bilgisi */}
      <Section title="Bağlantı Bilgileri" icon={Factory}>
        <div className="space-y-3">
          <div>
            <label className="label">Backend API URL</label>
            <div className="input bg-navy-50 font-mono text-xs text-navy-600 cursor-default select-all">
              {process.env.REACT_APP_API_URL || 'https://api.kanguroo.com.tr/api/v1'}
            </div>
          </div>
          <div className="text-xs text-navy-400 bg-navy-50 rounded-xl p-3">
            API URL'i değiştirmek için frontend'i yeniden build edin ve{' '}
            <code className="font-mono bg-navy-100 px-1 rounded">.env</code> dosyasında{' '}
            <code className="font-mono bg-navy-100 px-1 rounded">REACT_APP_API_URL</code> değerini güncelleyin.
          </div>
        </div>
      </Section>
    </div>
  );
}
